export const formatDate = (dateString?: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

/**
 * Formats a date string in a hydration-safe way by using UTC date components.
 * This ensures the same output on server and client, preventing hydration mismatches.
 * 
 * The key insight: we extract the UTC date components (year, month, day) and format
 * them consistently, avoiding timezone-dependent formatting that can differ between
 * server (often UTC) and client (user's local timezone).
 * 
 * @param dateString - ISO date string or date string
 * @param locale - Locale string (e.g., 'en', 'fi', 'sv')
 * @param options - Intl.DateTimeFormatOptions (defaults to short date format)
 * @returns Formatted date string or null if invalid
 */
export function formatDateHydrationSafe(
  dateString: string | undefined,
  locale: string,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' }
): string | null {
  if (!dateString) return null;
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    
    // Extract UTC date components to ensure consistency across server/client
    // This prevents timezone-related hydration mismatches
    const utcYear = date.getUTCFullYear();
    const utcMonth = date.getUTCMonth();
    const utcDay = date.getUTCDate();
    
    // Create a date at midnight UTC using the UTC components
    // This ensures the date represents the same calendar day regardless of timezone
    const utcDate = new Date(Date.UTC(utcYear, utcMonth, utcDay, 12, 0, 0));
    
    // Format using the locale, but the date itself is timezone-independent
    // The formatter will use the date's UTC components, ensuring consistency
    return new Intl.DateTimeFormat(locale, {
      ...options,
      timeZone: 'UTC', // Force UTC timezone to ensure server/client consistency
    }).format(utcDate);
  } catch (error) {
    return null;
  }
}
